<template>
  <v-container>
    <v-select
      v-model="currentChartType"
      :items="chartTypes"
      item-text="label"
      item-value="type"
      :label="labels.chartType"
    />

    <v-form v-model="valid" ref="statButtonsForm">
      <!-- subplot daily & yearly -->
      <v-row
        v-if="currentChartType !== 'plot-daily'"
        no-gutters
      >
        <v-autocomplete
          v-if="subplots"
          v-model="currentSubplots"
          :items="subplots"
          item-text="subplotLabel"
          :label="labels.subPlot"
          :search-input.sync="searchSubplots"
          :loading="loadingSubplots"
          no-filter
          hide-no-data
          multiple
          return-object
          disable-lookup
          :rules="[(v) => validate(v) || labels.subplotRequired ]"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip small v-if="index <= 1">
              <span class="d-inline-block text-truncate">
                {{ item.subplotLabel }}
              </span>
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text caption ml-2"
            >(+{{ currentSubplots.length - 2 }} {{ labels.more }})</span>
          </template>
        </v-autocomplete>
      </v-row>

      <!-- plot-daily -->
      <v-row
        v-else
        no-gutters
      >
        <v-autocomplete
          v-if="plots"
          v-model="currentPlots"
          :items="plots"
          :loading="loadingPlots"
          :search-input.sync="searchPlots"
          item-text="labelOrRefCatastral"
          :label="labels.plot"
          no-filter
          hide-no-data
          multiple
          return-object
          disable-lookup
          small-chips
        >
          <template v-slot:selection="{ item, index }">
            <v-chip small v-if="index <= 1">
              <span class="d-inline-block text-truncate">
                {{ item.labelOrRefCatastral }}
              </span>
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text caption ml-2"
            >(+{{ currentPlots.length - 2 }} {{ labels.more }})</span>
          </template>
        </v-autocomplete>

        <calendar
          v-model="dateRange"
          :disabled="false"
          :range="true"
        />

      </v-row>

      <!-- Selector de variables -->
      <v-autocomplete
        v-if="variables"
        color="primary"
        v-model="currentVars"
        deletable-chips
        multiple
        :items="filteredVars"
        item-text="name"
        :label="labels.variables"
        return-object
        :rules="[(v) => validate(v) || labels.variableRequired ]"
        small-chips
      />

      <v-btn
        color="primary"
        @click="addChart"
        :disabled="!valid"
        rounded
        small
        class="mt-3"
      >
        <v-icon class="mr-2" v-text="'$addChart'" />
        {{ labels.getData }}
      </v-btn>

    </v-form>
  </v-container>
</template>

<script>
/* eslint no-param-reassign: ["error", { "props": false }] */
import { mapActions } from 'vuex';
import { labels } from '@/assets/texts.json';
import { isArrEmpty } from '@/helpers';
import statsService from '@/services/stats.service';
import wmsService from '@/services/wms.service';
import debounce from 'lodash/debounce';

const Calendar = () => import('@/components/Calendar.vue');


export default {
  name: 'StatButtons',
  model: {
    prop: 'chartType',
    event: 'setChartType',
  },
  components: {
    Calendar,
  },
  props: {
    chartType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      chartTypes: [
        // subplot-yearly
        { type: 'yearly', label: labels.annualProd },
        // subplot-daily
        { type: 'daily', label: labels.annualTrack },
        // plot-daily
        { type: 'plot-daily', label: labels.trend },
      ],
      initialPlots: null,
      initialSubplots: null,
      plots: null,
      subplots: null,
      variables: null,
      currentChartType: this.chartType,
      currentPlots: null,
      currentSubplots: null,
      currentVars: null,
      searchPlots: null,
      searchSubplots: null,
      loadingPlots: false,
      loadingSubplots: false,
      fromDate: this.sixMonthsAgo(),
      labels,
      untilDate: this.today(),
      valid: false,
    };
  },
  methods: {
    ...mapActions({
      toggleLoading: 'shared/toggleLoading',
      toggleReady: 'shared/toggleReady',
    }),
    validate(Arr) {
      return !isArrEmpty(Arr);
    },
    addChart() {
      if (
        this.currentChartType === 'yearly'
        || this.currentChartType === 'daily'
      ) {
        this.$emit(
          'addChart',
          this.currentSubplots,
          this.currentVars,
        );
      } else {
        this.$emit(
          'addChart',
          this.currentPlots,
          this.fromDate,
          this.untilDate,
          this.currentVars,
        );
      }
    },
    getCurrentSubplots() {
      return this.subplots.filter((subplot) => (
        this.currentSubplots.some(
          (current) => subplot.subplotLabel === current.subplotLabel,
        )
      ));
    },
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    sixMonthsAgo() {
      const now = new Date();
      return new Date(now.setMonth(now.getMonth() - 6)).toISOString().substr(0, 10);
    },
    filterPlots: debounce((q, self) => {
      if (q) {
        self.loadingPlots = true;
        wmsService.getPlots(0, 100, q)
          .then((plots) => {
            self.plots = self.processPlots(plots.plots);
          })
          .finally(() => { self.loadingPlots = false; });
      } else {
        self.plots = self.initialPlots;
      }
    }, 300),
    filterSubplots: debounce((q, self) => {
      if (q) {
        self.loadingSubplots = true;
        statsService.getSubplots(0, 100, q)
          .then((subplots) => {
            self.subplots = self.flattenSubplots(subplots);
          })
          .finally(() => { self.loadingSubplots = false; });
      } else {
        self.subplots = self.initialSubplots;
      }
    }, 300),
    flattenSubplots(subplots) {
      return subplots.map(
        (plot) => {
          plot.plotLabel = plot.label
            ? `${plot.label} (ref. ${plot['ref-catastral']})`
            : plot['ref-catastral'];
          return plot.subplots.map((subplot) => {
            subplot.plotId = plot.id;
            subplot.subplotLabel = `${plot.plotLabel} - ${subplot.crop} - ${subplot.year}`;
            return subplot;
          });
        },
      ).flat();
    },
    processPlots(plots) {
      return plots.map((plot) => {
        plot.labelOrRefCatastral = plot['ref-catastral'];
        if (plot.label) {
          plot.labelOrRefCatastral = `${plot.label} (${plot['ref-catastral']})`;
        }
        return plot;
      });
    },
  },
  computed: {
    filteredVars() {
      if (!this.currentChartType) return null;
      const type = this.currentChartType.includes('daily')
        ? 'daily'
        : this.currentChartType;
      return this.variables.filter(
        (variable) => variable.type === type,
      );
    },
    dateRange: {
      get() {
        return [this.fromDate, this.untilDate];
      },
      set(value) {
        [this.fromDate, this.untilDate] = value.sort();
      },
    },
  },
  created() {
    this.currentChartType = 'yearly';
  },
  mounted() {
    this.toggleLoading(true);
    this.toggleReady(false);
    this.loadingPlots = true;
    this.loadingSubplots = true;
    statsService.getVars()
      .then((variables) => {
        this.variables = variables;
        return statsService.getSubplots();
      })
      .then((subplots) => {
        this.initialSubplots = this.flattenSubplots(subplots);
        this.subplots = this.initialSubplots;
        return wmsService.getPlots();
      })
      .then((plots) => {
        this.initialPlots = this.processPlots(plots.plots);
        this.plots = this.initialPlots;
      })
      .catch((error) => {
        console.log(error);
        this.$emit('error', this.errorMessages.unexpected);
      })
      .finally(() => {
        this.toggleLoading(false);
        this.loadingPlots = false;
        this.loadingSubplots = false;
      });
  },
  watch: {
    currentChartType() {
      this.$emit('setChartType', this.currentChartType);
      this.$refs.statButtonsForm.reset();
    },
    searchPlots(q) {
      this.plots = [];
      this.filterPlots(q, this);
    },
    searchSubplots(q) {
      this.subplots = [];
      this.filterSubplots(q, this);
    },
  },
};
</script>

<style>
.v-select{
  margin-right: 1vw;
}
.theme--light.v-chip, .mdi-close-circle::before{
  background: var(--v-primary-base) !important;
  color: white;
}
.v-chip{
  max-width: 10vw;
}
</style>
